import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * The token it produces is single-use and expires after ~5 minutes, so the
 * parent form must reset the widget (ref.current.reset()) after every failed
 * submit and request a fresh token.
 *
 * Site key comes from REACT_APP_TURNSTILE_SITE_KEY. When it is not set the
 * widget renders nothing and no token is sent - the backend is the single
 * source of truth for whether the check is enforced.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile script loaded but window.turnstile is missing'));
      }
    };

    const handleError = () => {
      scriptPromise = null;
      reject(new Error('Failed to load the Turnstile script'));
    };

    if (existing) {
      existing.addEventListener('load', handleLoad);
      existing.addEventListener('error', handleError);
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export const getTurnstileSiteKey = () => process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// REACT_APP_TURNSTILE_ENABLED=false hides the widget (unset = on). Turn the
// backend's TURNSTILE_ENABLED off together with it, or logins will be refused.
export const isTurnstileEnabled = () =>
  String(process.env.REACT_APP_TURNSTILE_ENABLED).toLowerCase() !== 'false';

export const isTurnstileConfigured = () => isTurnstileEnabled() && Boolean(getTurnstileSiteKey());

const Turnstile = forwardRef(({ action, onVerify, onExpire, onError, className = '' }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // Keep the latest callbacks in a ref so a parent re-render never re-creates
  // the widget (which would throw away a token the user already solved).
  const handlersRef = useRef({ onVerify, onExpire, onError });
  handlersRef.current = { onVerify, onExpire, onError };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }), []);

  useEffect(() => {
    const siteKey = getTurnstileSiteKey();

    if (!isTurnstileEnabled()) return undefined;

    if (!siteKey) {
      console.warn('[Turnstile] REACT_APP_TURNSTILE_SITE_KEY is not configured - widget not rendered');
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          callback: (token) => handlersRef.current.onVerify?.(token),
          'expired-callback': () => handlersRef.current.onExpire?.(),
          'timeout-callback': () => handlersRef.current.onExpire?.(),
          'error-callback': () => handlersRef.current.onError?.()
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('[Turnstile]', error.message);
        setLoadFailed(true);
        handlersRef.current.onError?.();
      });

    return () => {
      cancelled = true;

      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [action]);

  if (!isTurnstileConfigured()) {
    return null;
  }

  return (
    <div className={`turnstile-wrapper ${className}`.trim()}>
      <div ref={containerRef} className="turnstile-widget"></div>
      {loadFailed && (
        <span className="error-message">
          The security check could not load. Please refresh the page and try again.
        </span>
      )}
    </div>
  );
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
